import { CuToman, InfoCircleOutlined, Warning } from "@payping/icons";
import { DashboardRoutes } from "@/router/constant";
import { Button, Card, Col, Row, Space, Tooltip, Flex, Typography } from "@payping/web-components";
import { FC } from "react";
import { Link, generatePath, useNavigate } from "react-router-dom";
import { useAppSelector } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { ctaCreateId, depositCodeCreateId, listViewId } from "@/assets/googleTrackIds";
import { PagedQueryResultRequestDetailsVM } from "@payping/api";
import { CreateStepEnum } from "@/pages/Settlement/Create";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";

interface Props {
  loading?: boolean;
  unknownStatus?: PagedQueryResultRequestDetailsVM;
}

const DataCard: FC<Props> = (props) => {
  const { loading, unknownStatus } = props;
  const { t } = useAppTranslation();
  const { overview } = useAppSelector((state) => state.cashOuts);
  const navigate = useNavigate();

  return (
    <Row gutter={[8, 8]}>
      <Col xs={24} lg={8}>
        <Card.Ant loading={loading} className="tw-h-full">
          <Card.Meta
            title={
              <Flex align="center">
                <Typography.Text>{`${formatLocalizedNumber({ value: overview?.withdrawableBalance ?? 0 })}`}</Typography.Text>
                <CuToman width={24} height={24} />
              </Flex>
            }
            description={
              <Space direction={"vertical"} className={"tw-w-full"}>
                <div className={"tw-flex tw-justify-between"}>
                  {t("settlement.settableBalance")}
                  <Tooltip className="tw-align-middle" placement="top" title={t("settlement.desc.settableBalance")}>
                    <InfoCircleOutlined width={"14px"} />
                  </Tooltip>
                </div>
                <Space size={"middle"}>
                  <Button
                    disabled={Boolean(unknownStatus?.data?.length)}
                    type={"primary"}
                    onClick={() =>
                      navigate(
                        generatePath(DashboardRoutes.settlement.create.url, {
                          code: null,
                          step: String(CreateStepEnum.Configuration),
                        }),
                      )
                    }
                    data-google-track={ctaCreateId}
                  >
                    {t("settlement.create.title")}
                  </Button>
                  {unknownStatus?.data?.length ? (
                    <Tooltip placement={"leftBottom"} title={t("settlement.error.tooltipWarning")}>
                      <Warning color={"orange"} width={14} />
                    </Tooltip>
                  ) : null}
                </Space>
              </Space>
            }
          />
        </Card.Ant>
      </Col>
      <Col xs={24} lg={8}>
        <Card.Ant loading={loading} className="tw-h-full">
          <Card.Meta
            title={
              <Flex align="center">
                <Typography.Text>{`${formatLocalizedNumber({ value: overview?.balance ?? 0 })}`}</Typography.Text>
                <CuToman width={24} height={24} />
              </Flex>
            }
            description={
              <Space direction={"vertical"} className={"tw-w-full"}>
                <div className={"tw-flex tw-justify-between"}>
                  {t("settlement.totalBalance")}
                  <Tooltip className="tw-align-middle" placement="top" title={t("settlement.desc.totalBalance")}>
                    <InfoCircleOutlined width={"14px"} />
                  </Tooltip>
                </div>
                {overview?.isDepositActivate ? (
                  <Link to={DashboardRoutes.settlement.createId.url} data-google-track={depositCodeCreateId}>
                    <Button type={"link"} className={"!tw-p-0"}>
                      {t("settlement.newDepositID")}
                    </Button>
                  </Link>
                ) : null}
              </Space>
            }
          />
        </Card.Ant>
      </Col>
      <Col xs={24} lg={8}>
        <Card.Ant loading={loading} className="tw-h-full">
          <Card.Meta
            title={
              <Flex align="center">
                <Typography.Text>{`${formatLocalizedNumber({ value: overview?.totalWithdraw ?? 0 })}`}</Typography.Text>
                <CuToman width={24} height={24} />
              </Flex>
            }
            description={
              <Space direction={"vertical"} className={"tw-w-full"}>
                <div className={"tw-flex tw-justify-between"}>
                  {t("settlement.totalSettledAmount")}
                  <Tooltip className="tw-align-middle" placement="top" title={t("settlement.desc.totalSettledAmount")}>
                    <InfoCircleOutlined width={"14px"} />
                  </Tooltip>
                </div>

                <Link to={DashboardRoutes.settlement.report.list.url} data-google-track={listViewId}>
                  <Button type={"link"} className={"!tw-p-0"}>
                    {t("settlement.showSettlement")}
                  </Button>
                </Link>
              </Space>
            }
          />
        </Card.Ant>
      </Col>
    </Row>
  );
};

export default DataCard;
